import { useContext, useEffect, useState } from 'react'
import { ChevronRight, ExternalLink, Phone } from 'lucide-react'
import { getValue, ensureInitialized } from 'firebase/remote-config'
import ModalVideo from 'react-modal-video'
import recordIcon from '../../Assets/Images/HelpSupportShowcaseBg.png'
import { analytics, remoteConfig } from '../../firebase/firebase'
import { AuthContext } from '../../Context/AuthContext'
import { RemoteConfigKeys } from '../../firebase/remoteConfig'
import 'react-modal-video/scss/modal-video.scss'
import WhatsappIcon from '../../Assets/Images/whatsapp.svg'
import RequestCallbackDialog from '../../Components/Dialogs/RequestCallbackDialog'
import Controls from '../../Components/Controls/Controls'

const youtubeIdFromUrl = (url) => {
  try {
    return new URLSearchParams(new URL(url).search).get('v')
  } catch {
    return null
  }
}

const FaqList = ({ faqs, onOpen }) => (
  <ul className="edvi-help__faq-list">
    {faqs.map((value) => {
      const videoId = youtubeIdFromUrl(value.video_url)
      return (
        <li key={value.id}>
          <button
            type="button"
            className="edvi-help__faq-item"
            onClick={() => onOpen(videoId)}
          >
            <img
              className="edvi-help__faq-thumb"
              alt=""
              src={
                videoId
                  ? `https://img.youtube.com/vi/${videoId}/default.jpg`
                  : undefined
              }
            />
            <span className="edvi-help__faq-label">{value.action_title}</span>
            <ChevronRight size={18} className="edvi-help__faq-chevron" />
          </button>
        </li>
      )
    })}
  </ul>
)

const HelpAndSupport = () => {
  const { authState } = useContext(AuthContext)
  const [tutorFaqs, setTutorFaqs] = useState([])
  const [studentFaqs, setStudentFaqs] = useState([])
  const [launchPlayer, setLaunchPayer] = useState({ video: '', launch: false })
  const [launchRequestCallbackDialog, setLaunchRequestCallbackDialog] =
    useState(false)
  const policiesBaseUrl = 'https://policies.edvi.app'
  const role = authState?.role
  const showTutor =
    role === 'T' || role === null || role === undefined || role === ''
  const showStudent =
    role === 'S' || role === null || role === undefined || role === ''
  const showBothLabels = role === null || role === undefined || role === ''

  useEffect(() => {
    analytics.setUserProperties({
      name: authState.name,
      email: authState.email,
      role: authState.role,
      phone_number: authState.phone_number,
    })
  }, [])

  const fetchRemoteValues = () => {
    ensureInitialized(remoteConfig).then(() => {
      const remoteConfigValue = getValue(
        remoteConfig,
        RemoteConfigKeys.FAQS,
      ).asString()
      const configFaqs = JSON.parse(
        !remoteConfigValue ? '{}' : remoteConfigValue,
      )
      const tutorFaqsConfig =
        'tutor_faqs' in configFaqs ? configFaqs.tutor_faqs : []
      const studentFaqsConfig =
        'student_faqs' in configFaqs ? configFaqs.student_faqs : []
      if (tutorFaqsConfig !== null && tutorFaqsConfig.length !== 0) {
        setTutorFaqs(tutorFaqsConfig)
      }
      if (studentFaqsConfig !== null && studentFaqsConfig.length !== 0) {
        setStudentFaqs(studentFaqsConfig)
      }
    }, null)
  }

  useEffect(() => {
    fetchRemoteValues()
  }, [authState.role])

  const openFaqVideo = (videoId) => {
    if (!videoId) return
    setLaunchPayer({ video: videoId, launch: true })
  }

  return (
    <div className="edvi-help">
      <ModalVideo
        channel="youtube"
        autoplay
        isOpen={launchPlayer.launch}
        videoId={launchPlayer.video}
        onClose={() => setLaunchPayer({ video: '', launch: false })}
      />

      <RequestCallbackDialog
        open={launchRequestCallbackDialog}
        close={() => {
          setLaunchRequestCallbackDialog(false)
        }}
        email="asjbdaskd"
      />

      <section className="edvi-help__hero">
        <div className="edvi-help__hero-bg" aria-hidden>
          <img
            src={recordIcon || '/placeholder.svg'}
            alt=""
            height={500}
            width={500}
          />
        </div>
        <div className="edvi-help__hero-content">
          <h1 className="edvi-help__hero-title">Help and Support</h1>
          <p className="edvi-help__hero-sub">Hi, How can we help you today?</p>
        </div>
      </section>

      <div className="edvi-help__grid">
        <section className="edvi-help__panel edvi-help__panel--faq">
          <h2 className="edvi-help__section-title">Frequently Asked Questions</h2>

          {showBothLabels ? (
            <h3 className="edvi-help__group-title">Tutor Faqs</h3>
          ) : null}
          {showTutor ? (
            <FaqList faqs={tutorFaqs} onOpen={openFaqVideo} />
          ) : null}

          {showBothLabels ? (
            <h3 className="edvi-help__group-title">Student Faqs</h3>
          ) : null}
          {showStudent ? (
            <FaqList faqs={studentFaqs} onOpen={openFaqVideo} />
          ) : null}
        </section>

        <div className="edvi-help__sep" aria-hidden />

        <section className="edvi-help__panel">
          <h2 className="edvi-help__section-title">Still having query?</h2>

          <div className="edvi-help__callback">
            <Controls.Button
              variant="outlined"
              fullWidth={false}
              text="Request Callback"
              startIcon={<Phone size={18} />}
              onClick={() => setLaunchRequestCallbackDialog(true)}
            />
          </div>

          <div className="edvi-help__wa">
            <img
              src={WhatsappIcon || '/placeholder.svg'}
              alt=""
              width={20}
              height={20}
            />
            <span>WhatsApp</span>
            <a className="edvi-help__wa-link" href="tel:+919569768932">
              +919569768932
              <ExternalLink size={16} />
            </a>
          </div>

          <h3 className="edvi-help__group-title">Policies</h3>

          {showStudent ? (
            <>
              <a
                className="edvi-help__policy"
                href={`${policiesBaseUrl}/privacy`}
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy for Parents
              </a>
              <a
                className="edvi-help__policy"
                href={`${policiesBaseUrl}/terms`}
                target="_blank"
                rel="noreferrer"
              >
                Terms and Conditions for Parents
              </a>
            </>
          ) : null}

          {showTutor ? (
            <>
              <a
                className="edvi-help__policy"
                href={`${policiesBaseUrl}/tutor/terms`}
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy for Tutors
              </a>
              <a
                className="edvi-help__policy"
                href={`${policiesBaseUrl}/tutor/terms`}
                target="_blank"
                rel="noreferrer"
              >
                Terms and Conditions for Tutors
              </a>
            </>
          ) : null}

          <a
            className="edvi-help__policy"
            href={`${policiesBaseUrl}/terms#refund`}
            target="_blank"
            rel="noreferrer"
          >
            Cancellation and Refunds
          </a>

          <h3 className="edvi-help__group-title">Address</h3>
          <p className="edvi-help__address">Rz-1/15</p>
          <p className="edvi-help__address">
          Tughlakabad Extension, Near Alaknanda Market
          New Delhi
          </p>
        </section>
      </div>
    </div>
  )
}

export default HelpAndSupport
